/**
 * Utility functions for ingredient search feature.
 * All functions are pure and testable.
 */

/**
 * Filter autocomplete suggestions by prefix match, capped at maxResults.
 * Implements case-insensitive prefix matching.
 * @param {string} query - User input (2+ chars)
 * @param {Array<{uri: string, name: string}>} ingredients - Full ingredient list from API
 * @param {number} maxResults - Maximum suggestions to return (default 20)
 * @returns {Array<{uri: string, name: string}>} Filtered and capped suggestions
 */
export function filterAutocompleteSuggestions(query, ingredients, maxResults = 20) {
  if (!query || query.length < 2 || !Array.isArray(ingredients)) {
    return [];
  }

  const normalizedQuery = query.toLowerCase().trim();
  
  const filtered = ingredients.filter(ingredient => 
    ingredient && 
    ingredient.name && 
    ingredient.name.toLowerCase().startsWith(normalizedQuery)
  );

  return filtered.slice(0, maxResults);
}

/**
 * Filter products by selected ingredients and mode.
 * Used for client-side filtering when results are cached.
 * @param {Array<{uri: string, ingredients: Array<{uri: string}>}>} products - Products to filter
 * @param {string[]} selectedUris - Selected ingredient URIs
 * @param {'and'|'or'} mode - Combination mode
 * @returns {Array} Filtered products
 */
export function filterProductsByIngredients(products, selectedUris, mode) {
  if (!Array.isArray(products) || !Array.isArray(selectedUris) || selectedUris.length === 0) {
    return products || [];
  }

  const selectedSet = new Set(selectedUris);

  return products.filter(product => {
    if (!product || !Array.isArray(product.ingredients)) {
      return false;
    }

    const productIngredientUris = product.ingredients.map(ing => ing.uri);

    if (mode === 'and') {
      // AND mode: product must contain ALL selected ingredients
      return selectedUris.every(uri => productIngredientUris.includes(uri));
    } else {
      // OR mode: product must contain AT LEAST ONE selected ingredient
      return productIngredientUris.some(uri => selectedSet.has(uri));
    }
  });
}

/**
 * Classify which ingredients in a product match the user's selection.
 * @param {Array<{uri: string, name: string}>} productIngredients - Ingredients in the product
 * @param {string[]} selectedUris - User-selected ingredient URIs
 * @returns {Array<{uri: string, name: string, isMatch: boolean}>} Ingredients annotated with isMatch
 */
export function classifyMatchingIngredients(productIngredients, selectedUris) {
  if (!Array.isArray(productIngredients)) {
    return [];
  }

  const selectedSet = new Set(selectedUris || []);

  return productIngredients.map(ingredient => ({
    ...ingredient,
    isMatch: selectedSet.has(ingredient.uri)
  }));
}

/**
 * Serialize selected state to URL search params.
 * @param {Array<{uri: string, name: string}>} ingredients - Selected ingredients
 * @param {string} mode - Current mode ('and' or 'or')
 * @param {number} page - Current page
 * @returns {URLSearchParams}
 */
export function serializeStateToParams(ingredients, mode, page) {
  const params = new URLSearchParams();

  if (Array.isArray(ingredients) && ingredients.length > 0) {
    const uris = ingredients.map(ing => ing.uri).join(',');
    params.set('ingredients', uris);
  }

  if (mode && mode !== 'and') {
    // Only include mode if it's not the default 'and'
    params.set('mode', mode);
  }

  if (page && page > 1) {
    // Only include page if it's not the default page 1
    params.set('page', String(page));
  }

  return params;
}

/**
 * Deserialize URL search params to state.
 * @param {URLSearchParams} params
 * @returns {{uris: string[], mode: string, page: number}}
 */
export function deserializeParamsToState(params) {
  const ingredientsParam = params.get('ingredients');
  const modeParam = params.get('mode');
  const pageParam = params.get('page');

  // Parse ingredient URIs
  const uris = ingredientsParam 
    ? ingredientsParam.split(',').filter(uri => uri.trim().length > 0)
    : [];

  // Parse mode with validation, default to 'and'
  const mode = (modeParam === 'and' || modeParam === 'or') ? modeParam : 'and';

  // Parse page with validation, default to 1
  const parsedPage = parseInt(pageParam, 10);
  const page = (!isNaN(parsedPage) && parsedPage >= 1) ? parsedPage : 1;

  return {
    uris,
    mode,
    page
  };
}
